import { z } from 'zod';

/**
 * Structured chat branding (concept §10).
 *
 * Deliberately a closed set of structured settings. Arbitrary custom CSS is
 * excluded from V1, so every value below is either a validated colour, a value
 * from a fixed enum, or an opaque object key for an uploaded background.
 */

/** Fonts the product supports. Self-hosted or system; no remote font requests. */
export const supportedFonts = [
  'system',
  'inter',
  'ibm-plex-sans',
  'ibm-plex-mono',
  'newsreader',
  'georgia',
] as const;
export type SupportedFont = (typeof supportedFonts)[number];

/** Overall visual style applied to what both parties see. */
export const chatStyles = ['classic', 'soft', 'compact', 'contrast'] as const;
export type ChatStyle = (typeof chatStyles)[number];

/** Corner treatment for bubbles and controls. */
export const cornerStyles = ['sharp', 'rounded', 'pill'] as const;
export type CornerStyle = (typeof cornerStyles)[number];

const hexColour = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'expected a #rrggbb colour')
  .transform((value) => value.toLowerCase());

export const chatThemeSchema = z.object({
  /*
   * Defaults are the Kairelo palette (docs/brand.md §3). Only owners who have
   * never saved a theme see these: a stored theme keeps every value it was
   * saved with, because the row carries all of them.
   */
  /** Primary brand colour: owner bubbles, buttons, focus rings. */
  accentColour: hexColour.default('#246bfd'),
  /** Page background behind the conversation. */
  backgroundColour: hexColour.default('#f3f7fa'),
  /** Surface colour for bubbles, cards and inputs. */
  surfaceColour: hexColour.default('#ffffff'),
  /** Primary text colour. */
  textColour: hexColour.default('#172c4b'),
  font: z.enum(supportedFonts).default('system'),
  style: z.enum(chatStyles).default('classic'),
  corners: z.enum(cornerStyles).default('rounded'),
  /**
   * Opaque object key of an uploaded background image in the profile bucket.
   * Profile media is not end-to-end encrypted (concept §6).
   */
  backgroundImageKey: z.string().max(255).nullable().default(null),
});

export type ChatTheme = z.infer<typeof chatThemeSchema>;

export const defaultChatTheme: ChatTheme = chatThemeSchema.parse({});
